/**
 * Unified keyboard shortcut registry.
 *
 * All shortcuts register here. A single keydown listener dispatches.
 * The help dialog reads from the registry, so it's always in sync.
 */

import { useSyncExternalStore } from "react";

// ── Types ──

export type ShortcutCategory = "global" | "navigation" | "editing" | "module";

export interface ShortcutDef {
	/** Unique identifier, e.g. "global.agent-toggle" */
	id: string;
	/** Normalized key combo, e.g. "meta+j", "meta+shift+t" */
	keys: string;
	/** i18n key for label */
	label: string;
	/** Display string for UI, e.g. "⌘J" */
	displayKeys: string;
	/** Grouping category */
	category: ShortcutCategory;
	/** Handler */
	action: () => void;
	/** Optional predicate — skip if returns false */
	enabled?: () => boolean;
}

// ── Registry (plain Map, not zustand) ──

const registry = new Map<string, ShortcutDef>();
const listeners = new Set<() => void>();
let snapshot = Array.from(registry.values());

function notify() {
	snapshot = Array.from(registry.values());
	for (const l of listeners) l();
}

export function registerShortcut(def: ShortcutDef): () => void {
	registry.set(def.id, def);
	notify();
	return () => {
		registry.delete(def.id);
		notify();
	};
}

/** React hook — returns all registered shortcuts (for help dialog). */
export function useShortcutRegistry(): ShortcutDef[] {
	return useSyncExternalStore(
		(cb) => {
			listeners.add(cb);
			return () => listeners.delete(cb);
		},
		() => snapshot,
	);
}

/** Get a single shortcut by ID (for tooltip display). */
export function getShortcut(id: string): ShortcutDef | undefined {
	return registry.get(id);
}

// ── Key normalization ──

function normalizeEvent(e: KeyboardEvent): string {
	const parts: string[] = [];
	if (e.metaKey || e.ctrlKey) parts.push("meta");
	if (e.shiftKey) parts.push("shift");
	if (e.altKey) parts.push("alt");
	// Normalize key to lowercase, handle special cases
	const key = e.key.toLowerCase();
	if (key !== "meta" && key !== "control" && key !== "shift" && key !== "alt") {
		parts.push(key);
	}
	return parts.join("+");
}

// ── Dispatcher (attach once in __root.tsx) ──

function isEditableElement(el: Element | null): boolean {
	if (!el) return false;
	const tag = el.tagName;
	if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
	if ((el as HTMLElement).isContentEditable) return true;
	return false;
}

/** Build the key combo → shortcut lookup for fast dispatch. */
function buildLookup(): Map<string, ShortcutDef> {
	const map = new Map<string, ShortcutDef>();
	for (const def of registry.values()) {
		map.set(def.keys, def);
	}
	return map;
}

export function handleKeyDown(e: KeyboardEvent): void {
	const combo = normalizeEvent(e);
	if (!combo || combo === "meta" || combo === "shift" || combo === "alt") return;

	const lookup = buildLookup();
	const def = lookup.get(combo);
	if (!def) return;

	// Allow meta+key shortcuts even in editable elements (Cmd+K, Cmd+J, etc.)
	// But block non-meta shortcuts (like "/" or single keys) in editable elements
	if (!combo.includes("meta") && isEditableElement(document.activeElement)) return;

	if (def.enabled && !def.enabled()) return;

	e.preventDefault();
	def.action();
}

// ── Display helpers ──

const IS_MAC =
	typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);

export function formatKeys(keys: string): string {
	return keys
		.split("+")
		.map((k) => {
			switch (k) {
				case "meta":
					return IS_MAC ? "⌘" : "Ctrl";
				case "shift":
					return "⇧";
				case "alt":
					return IS_MAC ? "⌥" : "Alt";
				case "enter":
					return "↵";
				case "escape":
					return "Esc";
				case "tab":
					return "Tab";
				case "backspace":
					return "⌫";
				case "arrowup":
					return "↑";
				case "arrowdown":
					return "↓";
				case "arrowleft":
					return "←";
				case "arrowright":
					return "→";
				default:
					return k.toUpperCase();
			}
		})
		.join("");
}
